import React from "react";
import {
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import type { BreakpointKey, BreakpointWidthConfig, ResponsiveTableWidths } from "../../types";
import { BREAKPOINTS } from "../../types";
import { PERCENT_OPTIONS } from "../../utils/styleUtils";
import type { TableSettings } from "../UnifiedTableSettingsModal";

const breakpointIcons: Record<BreakpointKey, React.FC<{ className?: string }>> = {
  xs: Smartphone,
  sm: Tablet,
  md: Laptop,
  lg: Monitor,
  xl: Monitor,
};

interface TableSettingsTabProps {
  tableSettings: TableSettings;
  onTableSettingsChange: (settings: Partial<TableSettings>) => void;
  onApplyTableSettings: () => void;
  onResetColumnWidths: () => void;
  onClose: () => void;
  onCloseWithoutReopen: () => void;
}

const defaultBreakpointConfig: BreakpointWidthConfig = {
  type: "percent",
  value: "100",
  alignment: "left",
  horizontalScroll: false,
  // rowGap is intentionally not set here so breakpoints inherit from xs
};

export const TableSettingsTab: React.FC<TableSettingsTabProps> = ({
  tableSettings,
  onTableSettingsChange,
  onApplyTableSettings,
  onResetColumnWidths,
  onClose,
  onCloseWithoutReopen,
}) => {
  // Helper to update a specific breakpoint's width config
  const updateBreakpointWidth = (
    breakpoint: BreakpointKey,
    updates: Partial<BreakpointWidthConfig>
  ) => {
    const currentWidths = tableSettings.tableWidths || {};
    const currentConfig = currentWidths[breakpoint] || defaultBreakpointConfig;
    onTableSettingsChange({
      tableWidths: {
        ...currentWidths,
        [breakpoint]: { ...currentConfig, ...updates },
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Table Width */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Monitor className="w-4 h-4" />
          Table Container Settings (Per Breakpoint)
        </h4>
        
        <div className="space-y-3">
          {/* Per-breakpoint Controls */}
          {(Object.keys(BREAKPOINTS) as BreakpointKey[]).map((key) => {
            const Icon = breakpointIcons[key];
            const bp = BREAKPOINTS[key];
            const config = tableSettings.tableWidths?.[key] || defaultBreakpointConfig;
            
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                {/* Breakpoint Header */}
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <Icon className="w-4 h-4 text-primary-main" />
                  <span className="font-semibold text-sm text-gray-800">{key.toUpperCase()}</span>
                  <span className="text-xs text-gray-500">({bp.description})</span>
                </div>
                
                <div className="space-y-3">
                  {/* Width Row */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-medium text-gray-600 w-16">Width:</label>
                    
                    {/* Width Type Selector */}
                    <div className="flex gap-1">
                      {(["percent", "pixel"] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updateBreakpointWidth(key, { type, value: config.value || (type === "percent" ? "100" : "800") })}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                            config.type === type
                              ? "bg-primary-main text-white"
                              : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {type === "percent" ? "%" : "px"}
                        </button>
                      ))}
                    </div>

                    {/* Value Input */}
                      <div className="flex-1 max-w-[120px]">
                        {config.type === "percent" ? (
                          <select
                            value={config.value}
                            onChange={(e) => updateBreakpointWidth(key, { value: e.target.value })}
                            className="w-full border border-gray-300 px-2 py-1.5 rounded text-sm bg-white"
                          >
                            <option value="">Select %</option>
                            {PERCENT_OPTIONS.filter(opt => opt.value !== "").map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="number"
                            min="100"
                            max="2000"
                            placeholder="px"
                            value={config.value}
                            onChange={(e) => updateBreakpointWidth(key, { value: e.target.value })}
                            className="w-full border border-gray-300 px-2 py-1.5 rounded text-sm"
                          />
                        )}
                      </div>
                  </div>

                  {/* Alignment Row */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-medium text-gray-600 w-16">Align:</label>
                    <div className="flex gap-1">
                      {([
                        { value: "left" as const, icon: AlignLeft },
                        { value: "center" as const, icon: AlignCenter },
                        { value: "right" as const, icon: AlignRight },
                      ]).map(({ value, icon: AlignIcon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateBreakpointWidth(key, { alignment: value })}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1 capitalize ${
                            config.alignment === value
                              ? "bg-primary-main text-white"
                              : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          <AlignIcon className="w-3 h-3" />
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Row Gap & Horizontal Scroll Row */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-medium text-gray-600 w-16">Row Gap:</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="0"
                      value={config.rowGap ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateBreakpointWidth(key, { rowGap: val === "" ? undefined : parseInt(val) });
                      }}
                      className="w-16 border border-gray-300 px-2 py-1.5 rounded text-sm"
                    />
                    <span className="text-xs text-gray-400">px</span>
                    
                    <span className="mx-2 text-gray-300">|</span>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.horizontalScroll}
                        onChange={(e) => updateBreakpointWidth(key, { horizontalScroll: e.target.checked })}
                        className="w-4 h-4 text-primary-main border-gray-300 rounded"
                      />
                      <span className="text-xs text-gray-600">Horizontal scroll</span>
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Border Radius */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Border Radius (px)</label>
          <input
            type="number"
            min="0"
            max="50"
            value={tableSettings.tableBorderRadius}
            onChange={(e) => onTableSettingsChange({ tableBorderRadius: parseInt(e.target.value) || 0 })}
            className="w-20 border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <button
          type="button"
          onClick={onResetColumnWidths}
          className="px-4 py-2 border border-orange-300 text-orange-600 rounded-md text-sm font-medium hover:bg-orange-50"
        >
          Reset All Widths
        </button>
        <button
          type="button"
          onClick={() => {
            onCloseWithoutReopen();
            onApplyTableSettings();
          }}
          className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90"
        >
          Apply Table Settings
        </button>
      </div>
    </div>
  );
};

